import * as React from "react";
import { useEffect, useState } from "react";
import {
  Button,
  ButtonColor,
  ButtonSize,
  ButtonVariant,
  SelectOption,
  Spinner,
  Text,
  TextField,
  TextVariant,
} from "../../ui";
import { FullScreenDialog } from "../../FullScreenDialog";
import { PartOrder } from "../../../generated/nest-graphql";
import { PART_ORDERING_ISSUE_OPTIONS } from "../partsStatus.utils";

/**
 * Reports a problem against an order using the same reason codes ops has always had,
 * plus free text.
 *
 * The notes land in `partOrderingIssueNotes`, not `notes` — `notes` is "Part Notes",
 * written by ops and shown back to technicians in the appointment summary, so putting
 * complaints there would overwrite something people rely on.
 */
export const ReportIssueDialog: React.FC<{
  order: PartOrder | null;
  open: boolean;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (issue: string, notes: string) => void;
}> = ({ order, open, submitting, onClose, onSubmit }) => {
  const [issue, setIssue] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (open) {
      setIssue(order?.partOrderingIssue ?? "");
      setNotes("");
    }
  }, [open, order?.id, order?.partOrderingIssue]);

  return (
    <FullScreenDialog title="Report a problem" open={open} onClose={submitting ? undefined : onClose}>
      <div className="p-4 flex flex-col gap-4">
        <div>
          <Text variant={TextVariant.BODY2} className="text-gray-400">
            Order
          </Text>
          <Text variant={TextVariant.H6}>{order?.orderNumber || "No order number"}</Text>
          <Text variant={TextVariant.BODY2} className="text-gray-400">
            {order?.store?.name}
          </Text>
        </div>

        <TextField select label="What went wrong?" value={issue} onChange={setIssue} fullWidth disabled={submitting}>
          {PART_ORDERING_ISSUE_OPTIONS.map((option) => (
            <SelectOption key={option} value={option}>
              {option}
            </SelectOption>
          ))}
        </TextField>

        <TextField
          label="Anything else? (optional)"
          value={notes}
          onChange={setNotes}
          fullWidth
          multiline
          rows={4}
          disabled={submitting}
        />

        <Text variant={TextVariant.BODY2} className="text-gray-400">
          This flags the order for HQ. It will not change your schedule or the job.
        </Text>

        <Button
          variant={ButtonVariant.CONTAINED}
          color={ButtonColor.PRIMARY}
          size={ButtonSize.LARGE}
          disabled={submitting || !issue}
          onClick={() => onSubmit(issue, notes)}
        >
          {submitting ? <Spinner size={22} /> : "Send report"}
        </Button>
      </div>
    </FullScreenDialog>
  );
};
